import { Form } from "antd";
import { forwardRef, useImperativeHandle, useState, useEffect, useRef } from "react";
import Select from "@/common/elements/InputElements/Select";
import TextField from "@/common/elements/InputElements/TextField";
import { useSelector } from "react-redux";
import {
  StyledGridCol,
  StyledGridRow,
  StyledInformationList,
  StyledSectionHeading,
} from "@/common/styles";
import CommonReportActionHeader from "@/components/CommonReport/CommonReportActionHeader";
import { StyledReportContainer } from "@/components/CommonReport/styles";
import CustomModal from "@/components/CustomModal";
import useDialog from "@/hooks/useDialog";
import {
  getOptionsFromRefList,
  getRefListNameByID,
  convertApiFields,
  getSQLTypeToHTMLType,
} from "@/util/common_function";
import { SCHEMA_NAME } from "@/util/common_variable";
import { FieldSecurityProvider } from "@/contexts/FieldSecurityContext";
import SecureFormItem from "@/common/SecureFormItem";
import dayjs from "dayjs";
import { exportReceiptPDF, exportReceiptHTML } from "@/util/exportReceipt";
import DynamicForm from "@/common/DynamicForm";
import { BookingsSectionTitle } from "@/components/BookingsDetails/styles";
import api from "@/services/axios_custom";
import API_ENDPOINTS from "@/util/api";

const CashAccountPaymentForm = forwardRef(({ depositFormRef, startingPoint = "" }, ref) => {
  const [form] = Form.useForm();

  const [reportModal, setReportModal] = useState(false);
  const { showDialog } = useDialog();
  const { bookingsFieldsEnabled } = useSelector((state) => state?.bookings);
  const { userDetail } = useSelector((s) => s?.userDetail);

  useEffect(() => {
    if (userDetail?.staffId) {
      form.setFieldsValue({ receivedBy: userDetail.staffId });
    }
  }, [userDetail]);

  // -----------------------------------------------------------------------
  // CashAccountingNewTransactionsExtra — PCI's per-client dynamic "_U"
  // columns for this transaction (CashAccountingNewTransactionsInterface.asp
  // 's "Additional Transaction Information" section, shown right after
  // Transaction Details). This form only ever creates a brand new
  // transaction, so there's nothing to fetch here — see
  // CashAccountDetailForm.jsx for the editing/viewing side.
  // -----------------------------------------------------------------------
  const [transactionExtraFields, setTransactionExtraFields] = useState([]);
  const transactionExtraTypeMapRef = useRef({});

  useEffect(() => {
    api
      .get(`${API_ENDPOINTS.getTableColumns}/CashAccountingNewTransactionsExtra`)
      .then((response) => {
        const tableData = (response?.data || []).filter((d) =>
          d?.columnName?.endsWith("_U")
        );
        const converted = convertApiFields(
          tableData,
          "CashAccountingNewTransactionsExtra"
        );
        const typeMap = {};
        converted.forEach((c) => {
          typeMap[c.columnName] = getSQLTypeToHTMLType(c?.type);
        });
        transactionExtraTypeMapRef.current = typeMap;
        setTransactionExtraFields(converted);
      })
      .catch((error) => {
        console.error(
          "[CashAccountPaymentForm] CashAccountingNewTransactionsExtra field defs fetch failed:",
          error
        );
      });
  }, []);

  const onConfirmReceipt = (cnf) => {
    if (cnf) {
      setReportModal(true);
    }
  };

  const onCloseHandler = () => {
    setReportModal(false);
  };

  const handleReceipt = () => {
    showDialog("confirm", {
      title: "Enter the name for the receipt.",
      confirmButtonText: "Okay",
      cancelButtonText: "Cancel",
      promptType: "text",
      onConfirm: (cnf) => onConfirmReceipt(cnf),
      prompt: true,
    });
  };

  const handleAmountKeyDown = (e) => {
    const { key, ctrlKey, metaKey } = e;
    const allowedControlKeys = [
      "Backspace", "Delete", "ArrowLeft", "ArrowRight",
      "ArrowUp", "ArrowDown", "Tab", "Enter",
      "Home", "End", "Escape", "CapsLock", "Shift", "Control",
    ];
    if (allowedControlKeys.includes(key) || ctrlKey || metaKey) return;
    if (key === "." && !e.target.value.includes(".")) return;
    if (!/^[0-9]$/.test(key)) {
      e.preventDefault();
    }
  };

  useImperativeHandle(depositFormRef, () => ({
    submitForm: () => form.submit(),
    resetForm: () => form.resetFields(),
    getFormValues: () => form.getFieldsValue(),
    setFormValue: () => form.setFieldValue(),
    getFieldsError: () => form.getFieldsError(),
    isFieldsValidating: () => form.isFieldsValidating(),
    validateFields: () => form.validateFields(),
    transactionExtraFieldKeys: transactionExtraFields.map((f) => f.columnName),
    transactionExtraTypeMap: transactionExtraTypeMapRef.current,
    transactionExtraID: 0, // always a brand new transaction from this form
  }));

  // Read form values when opening receipt modal
  const receiptValues = form.getFieldsValue();

  const buildReceiptData = () => {
    const values = form.getFieldsValue();
    return [
      { key: "Date",          value: dayjs().format("MM/DD/YYYY HH:mm") },
      { key: "Received From", value: getRefListNameByID("", userDetail?.staffId, true, true) },
      { key: "Amount",        value: values.amount },
      { key: "Check #",       value: values.checkNumber },
      { key: "Type",          value: values.depositType },
      { key: "Category",      value: values.category },
      { key: "Receipt #",     value: values.receiptNumber },
      { key: "Details",       value: values.transactionDetails },
      { key: "Received By",   value: getRefListNameByID("", values.receivedBy, true, true) },
    ];
  };

  const handleExportClick = (exportKey) => {
    const data = buildReceiptData();
    if (exportKey === "export-pdf") {
      exportReceiptPDF(data);
    } else if (exportKey === "export-html") {
      exportReceiptHTML(data);
    }
  };

  const handlePrintClick = () => {
    window.print();
  };

  return (
    <>
      <FieldSecurityProvider
        entity={SCHEMA_NAME.CASH_ACCOUNTING_NEW_TRANSACTIONS}
        startingPoint={startingPoint}
        bookingsFieldsEnabled={bookingsFieldsEnabled}
      >
        <Form form={form}>
          <StyledSectionHeading>
            <span>Please enter the amount of money being deposited</span>
          </StyledSectionHeading>

          <StyledGridRow gutter={16}>
            <StyledGridCol lg={4}>
              <SecureFormItem
                fieldName="amount"
                formItemProps={{
                  name: "amount",
                  label: "Amount",
                  rules: [{ required: true, message: "Amount is required" }],
                }}
              >
                <TextField prefix="$" onKeyDown={handleAmountKeyDown} />
              </SecureFormItem>
            </StyledGridCol>

            <StyledGridCol lg={4}>
              <SecureFormItem
                fieldName="checkNumber"
                formItemProps={{ name: "checkNumber", label: "Check #" }}
              >
                <TextField />
              </SecureFormItem>
            </StyledGridCol>

            <StyledGridCol lg={5}>
              <SecureFormItem
                fieldName="category"
                formItemProps={{ name: "category", label: "Category" }}
              >
                <Select
                  placeholder="Category"
                  variant="outlined"
                  options={getOptionsFromRefList("Category")}
                />
              </SecureFormItem>
            </StyledGridCol>

            <StyledGridCol lg={4}>
              <SecureFormItem
                fieldName="receiptNumber"
                formItemProps={{ name: "receiptNumber", label: "Receipt #" }}
              >
                <TextField />
              </SecureFormItem>
            </StyledGridCol>

            <StyledGridCol>
              <SecureFormItem
                fieldName="depositType"
                formItemProps={{
                  name: "depositType",
                  label: "Deposit Type",
                  rules: [{ required: true, message: "Deposit Type is required" }],
                }}
              >
                <Select
                  placeholder="Deposit Type"
                  variant="outlined"
                  options={getOptionsFromRefList("DepositType")}
                />
              </SecureFormItem>
            </StyledGridCol>
          </StyledGridRow>

          <StyledGridRow gutter={16}>
            <StyledGridCol lg={12}>
              <SecureFormItem
                fieldName="receivedBy"
                formItemProps={{ name: "receivedBy", label: "Received By" }}
              >
                <Select
                  placeholder="Received By"
                  variant="outlined"
                  options={getOptionsFromRefList("", true, true, true)}
                />
              </SecureFormItem>
            </StyledGridCol>

            <StyledGridCol lg={16}>
              <SecureFormItem
                fieldName="transactionDetails"
                formItemProps={{ name: "transactionDetails", label: "Transaction Details" }}
              >
                <TextField />
              </SecureFormItem>
            </StyledGridCol>
          </StyledGridRow>

          {/* Receipt button wired up */}
          <StyledGridRow>
            <StyledGridCol>
              <button type="button" onClick={handleReceipt}>
                Print Receipt
              </button>
            </StyledGridCol>
          </StyledGridRow>

          {transactionExtraFields.length > 0 && (
            <>
              <BookingsSectionTitle>
                <span>Additional Transaction Information</span>
              </BookingsSectionTitle>
              <StyledGridRow gutter={16}>
                {transactionExtraFields.map((sf, index) => (
                  <DynamicForm
                    key={index}
                    formAttributes={sf}
                    form={form}
                    bookingsFieldsEnabled={bookingsFieldsEnabled}
                  />
                ))}
              </StyledGridRow>
            </>
          )}
        </Form>
      </FieldSecurityProvider>

      {reportModal && (
        <CustomModal
          size="xl"
          show={reportModal}
          onHide={() => setReportModal(false)}
          header="BI2 Technologies Report"
          hideFooter={true}
        >
          <CommonReportActionHeader
            onClose={onCloseHandler}
            onExportClick={handleExportClick}
            handlePrintClick={handlePrintClick}
          />

          <StyledReportContainer>
            <div className="report-top-header">
              <div>
                <small>BI2 technologies - Receipt</small>
                <div>ANOKA COUNTY JAIL ANOKA, MN</div>
              </div>
            </div>
            <StyledInformationList>
              <div className="report-box">
                <div className="d-flex-item">
                  <h6 className="key">Date:</h6>
                  <h6 className="value">{dayjs().format("MM/DD/YYYY HH:mm")}</h6>
                </div>
                <div className="d-flex-item">
                  <h6 className="key">Received From:</h6>
                  <h6 className="value">{getRefListNameByID("", userDetail?.staffId, true, true)}</h6>
                </div>
                <div className="d-flex-item">
                  <h6 className="key">Amount:</h6>
                  <h6 className="value">{receiptValues?.amount}</h6>
                </div>
                <div className="d-flex-item">
                  <h6 className="key">Check #:</h6>
                  <h6 className="value">{receiptValues?.checkNumber}</h6>
                </div>
                <div className="d-flex-item">
                  <h6 className="key">Type:</h6>
                  <h6 className="value">{receiptValues?.depositType}</h6>
                </div>
                <div className="d-flex-item">
                  <h6 className="key">Category:</h6>
                  <h6 className="value">{receiptValues?.category}</h6>
                </div>
                <div className="d-flex-item">
                  <h6 className="key">Receipt #:</h6>
                  <h6 className="value">{receiptValues?.receiptNumber}</h6>
                </div>
                <div className="d-flex-item">
                  <h6 className="key">Details:</h6>
                  <h6 className="value">{receiptValues?.transactionDetails}</h6>
                </div>
                <div className="d-flex-item">
                  <h6 className="key">Received By:</h6>
                  <h6 className="value">{getRefListNameByID("", receiptValues?.receivedBy, true, true)}</h6>
                </div>
              </div>
            </StyledInformationList>
          </StyledReportContainer>
        </CustomModal>
      )}
    </>
  );
});

export default CashAccountPaymentForm;